import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Select, Spin, Alert, Tag, Statistic, Row, Col, Empty } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { API } from '@/services/api.service';

/**
 * Convert a work-type enum argb color ("cc99ff" or "FF000000") to a CSS hex.
 * 8-char argb has a leading alpha pair we drop; 6-char is plain rgb.
 */
const toHex = (argb, fallback) => {
  if (!argb || typeof argb !== 'string') return fallback;
  const rgb = argb.length === 8 ? argb.slice(2) : argb;
  return `#${rgb}`;
};

/**
 * Shared, reusable "other leaves" history view for a single employee — every
 * leave type EXCEPT annual leave (which has its own balance-oriented view).
 * Renders duration stats, a per-type distribution chart, a seasonality
 * (month-of-year) chart, and a chronological requests table. Filterable by
 * year and leave type. Used on the employee profile (view + edit).
 */
export default function OtherLeavesHistoryView({ employeeId }) {
  const { t } = useTranslation(['translations']);
  const th = (key, opts) =>
    t(`otherLeavesHistory.${key}`, { ns: 'translations', ...opts });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [types, setTypes] = useState([]);

  useEffect(() => {
    if (!employeeId) {
      setData(null);
      return undefined;
    }
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await API.employees.getEmployeeOtherLeavesHistory(
          employeeId,
          { year: year || undefined, types: types.length ? types : undefined }
        );
        if (active) setData(res);
      } catch (err) {
        if (active) setError(err?.response?.data?.message || err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [employeeId, year, types]);

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '-';
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${dt.getFullYear()}.`;
  };

  const columns = [
    {
      title: th('colType'),
      dataIndex: 'typeLabel',
      key: 'type',
      width: 220,
      render: (label, e) => {
        const bg = toHex(e.color);
        return (
          <Tag
            style={
              bg
                ? {
                    backgroundColor: bg,
                    color: toHex(e.textColor) || '#000',
                    border: 'none',
                  }
                : undefined
            }
          >
            {label}
          </Tag>
        );
      },
    },
    {
      title: th('colPeriod'),
      key: 'period',
      width: 180,
      render: (_, e) =>
        e.dateFrom
          ? `${formatDate(e.dateFrom)}${
              e.dateTo && e.dateTo !== e.dateFrom
                ? ` – ${formatDate(e.dateTo)}`
                : ''
            }`
          : '-',
    },
    {
      title: th('colDays'),
      dataIndex: 'days',
      key: 'days',
      width: 90,
      render: (d) => (d != null ? d : '-'),
    },
  ];

  if (!employeeId) {
    return <Empty description={th('pickEmployee')} />;
  }

  const yearOptions = [
    { value: '', label: th('allYears') },
    ...(data?.years || []).map((y) => ({ value: y, label: y })),
  ];
  const typeOptions = (data?.types || []).map((tp) => ({
    value: tp.key,
    label: tp.label,
  }));

  const stats = data?.durationStats || {};

  return (
    <Spin spinning={loading}>
      <div className="space-y-4">
        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-medium">{th('year')}:</span>
          <Select
            value={year || ''}
            onChange={(v) => setYear(v || null)}
            options={yearOptions}
            style={{ width: 150 }}
          />
          <span className="font-medium">{th('type')}:</span>
          <Select
            mode="multiple"
            allowClear
            value={types}
            onChange={setTypes}
            options={typeOptions}
            placeholder={th('allTypes')}
            maxTagCount="responsive"
            style={{ minWidth: 240 }}
          />
        </div>

        {data && (
          <>
            <Card size="small" title={th('summaryTitle')} className="shadow-sm">
              <Row gutter={16}>
                <Col xs={12} sm={6}>
                  <Statistic
                    title={th('statPeriods')}
                    value={stats.totalPeriods ?? 0}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title={th('statTotalDays')}
                    value={stats.totalDays ?? 0}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title={th('statAvgDays')}
                    value={stats.avgDays ?? 0}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title={th('statMaxDays')}
                    value={stats.maxDays ?? 0}
                  />
                </Col>
              </Row>
            </Card>

            {data.byType?.length > 0 && (
              <Card size="small" title={th('byTypeTitle')} className="shadow-sm">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={data.byType}
                    margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" height={40} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ paddingTop: 12 }} />
                    <Bar
                      dataKey="totalDays"
                      name={th('seriesDays')}
                      fill="#1677ff"
                      maxBarSize={80}
                    />
                    <Bar
                      dataKey="count"
                      name={th('seriesCount')}
                      fill="#fa8c16"
                      maxBarSize={80}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}

            {data.byType?.length > 0 && (
              <Card
                size="small"
                title={th('seasonalityTitle')}
                className="shadow-sm"
              >
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={data.byMonth}
                    margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthLabel" height={40} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    {/* Keep the colored swatch but render the label text in a
                        neutral dark color — series colors like yellow are
                        unreadable as text on white. */}
                    <Legend
                      wrapperStyle={{ paddingTop: 12 }}
                      formatter={(value) => (
                        <span style={{ color: 'rgba(0,0,0,0.75)' }}>{value}</span>
                      )}
                    />
                    {/* One stacked segment per leave type, each in its enum color
                        — so different types are distinguishable per month. */}
                    {(data.types || []).map((tp) => (
                      <Bar
                        key={tp.key}
                        dataKey={tp.key}
                        name={tp.label}
                        stackId="months"
                        fill={toHex(tp.color, '#52c41a')}
                        maxBarSize={48}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}

            <Card size="small" title={th('eventsTitle')} className="shadow-sm">
              {data.events?.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={data.events}
                  rowKey="id"
                  pagination={{ pageSize: 20, showSizeChanger: true }}
                  scroll={{ x: 760 }}
                  size="small"
                />
              ) : (
                <Empty description={th('noData')} />
              )}
            </Card>
          </>
        )}
      </div>
    </Spin>
  );
}
